import { useMemo } from 'react';
import { useGetPaymentMethodsQuery } from '@/store/api/webPaymentsCompat';
import type { PaymentMethod } from '@/components/residents/redesign/payments';

/**
 * Shared hook that fetches and normalizes payment methods into the redesign PaymentMethod shape.
 * Replaces duplicated normalization logic across ResidentPaymentsScreen, DesktopPaymentsView, etc.
 */
export const usePaymentMethodsList = (): {
  methods: PaymentMethod[];
  rawMethods: any[];
  isLoading: boolean;
  refetch: () => void;
} => {
  const { data: methodsData, isLoading, refetch } = useGetPaymentMethodsQuery();

  const rawMethods: any[] = useMemo(() => {
    return (methodsData as any)?.response || [];
  }, [methodsData]);

  const methods = useMemo<PaymentMethod[]>(() => {
    return rawMethods.map((m: any) => {
      const type = (m.type || '').toLowerCase();
      const isBank = type === 'bank' || type === 'ach' || type === 'bankaccount';
      const brandRaw = (m.brand || m.accttype || m.name || '').toLowerCase();

      let brand: PaymentMethod['brand'] = 'unknown';
      if (isBank) brand = 'bank';
      else if (brandRaw.includes('visa')) brand = 'visa';
      else if (brandRaw.includes('master')) brand = 'mastercard';
      else if (brandRaw.includes('amex')) brand = 'amex';
      else if (brandRaw.includes('discover')) brand = 'discover';
      else if (brandRaw.includes('diners') || brandRaw === 'dnr') brand = 'diners';
      else if (brandRaw === 'jcb') brand = 'jcb';
      else if (brandRaw.includes('maestro') || brandRaw === 'maes') brand = 'maestro';
      else if (brandRaw.includes('union') || brandRaw === 'up') brand = 'unionpay';

      const label = isBank
        ? m.bankName || m.name || 'Bank Account'
        : brand === 'unknown'
          ? (m.name || 'Card')
          : brand.charAt(0).toUpperCase() + brand.slice(1);

      return {
        id: String(m.sourceId || m.id),
        brand,
        label,
        lastFour: m.last4 || '****',
        isDefault: !!m.isDefault,
        isExpired: !!m.isExpired,
        sourceId: m.sourceId || m.id,
        type: isBank ? 'ach' as const : 'card' as const,
      };
    });
  }, [rawMethods]);

  return { methods, rawMethods, isLoading, refetch };
};
